/**
 * All Projects — the admin cross-project oversight screen.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  IA FIX 2026-05-21 — AVBP_DATA_MODEL_IA_SPEC_v1 §5 / Q2.
 * ─────────────────────────────────────────────────────────────────────────
 *  This is the flat cross-project list that USED to be (wrongly) the
 *  "Clients" screen. Per spec Q2 it is KEPT — correctly named "All
 *  Projects" and repositioned as the admin oversight view across every
 *  engagement. The client-grouped "Clients" screen (Clients.tsx) now sits
 *  above it; a client owns N projects, and Client Detail is the per-client
 *  view.
 *
 *  Admin sees every project; staff sees only their assigned projects.
 *  Client never reaches this route (RequireAuth roles gate). Every row
 *  reads the locked engagementTemplate for tier/stage; money column is
 *  admin/staff only (visibilityFor).
 *
 *  The per-row edit panel (client master fields + the project's
 *  tier/value/assignee/stage), the confirm-gated Archive (NOT delete —
 *  rules forbid hard delete; archive = `archived:true`) and the
 *  include-archived toggle are kept here — they belong to the project
 *  oversight surface. The "+ New Client" affordance moved to the Clients
 *  screen; the per-client "+ New Project" lives on Client Detail.
 *  No firestore.rules change. Additive only.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { Btn3, Card, EmptyNote, ListScreen, StageRail, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import { RelinkProjectPanel } from '../components/RelinkProject';
import { ClientPicker } from '../components/ClientPicker';
import {
  seedProjects,
  DEMO_STAFF,
  type DemoProject,
  inr,
  matchesClientSearch,
  moneyViewOf,
  railOf,
  templateOf,
} from '../lib/trackDemo';
import { canonicalServiceNameFor, canonicalTierOptions } from '../lib/leadQuote';
import type { Tier } from '../lib/engagementTemplate';
import {
  listenProjects,
  updateProject,
  relinkProject,
  relabelProjectVp,
  validateVpEdit,
  archiveProject,
  trashProject,
  getClient,
  updateClient,
  projectLabel,
  type ClientRow,
  type LiveProject,
  type LiveClient,
} from '../lib/projectStore';
import { useClientNames } from '../lib/useClientNames';
import { canManageTrash } from '../lib/trash';

/** A live converted project → the DemoProject shape the track screens
 *  render (templateOf/moneyViewOf/railOf only need these fields). */
function liveToRow(p: LiveProject): DemoProject {
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tier: p.tierCode,
    activeStageKey: p.activeStageKey,
    assigneeUid: p.assigneeUid,
    assigneeName: p.assigneeName,
    valueINR: p.valueINR,
    clearedGateIds: p.clearedGateIds,
    openProposals: 0,
  };
}

export default function AllProjects() {
  const { role, user } = useAuth();
  const { toast } = useToast();
  const vis = visibilityFor(role);
  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'You';

  // LIVE Firestore-backed list of real projects (converted leads + the
  // imported master). The demo scaffold rows are appended ONLY when the
  // demo seed is explicitly enabled (#992 — default off → live data only).
  const [live, setLive] = useState<LiveProject[]>([]);
  // Canonical client name — the project's `clientName` is only a display
  // cache and can drift; the `clients/{id}` master is the single source of
  // truth (AVBP_DATA_MODEL_IA_SPEC_v1). Resolve every row's client name
  // from the master so a client never shows two different names.
  const { resolveName } = useClientNames();
  // Three-state load model (item 9) — empty note shows only post-fetch.
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // Which row's RE-LINK panel is open (item 1 — distinct from `editing`).
  const [relinking, setRelinking] = useState<string | null>(null);
  // Live client-side search. `search` tracks the input every keystroke
  // (responsive field); `query` is the debounced value the list filters
  // on, so a long list does not re-filter on every single keypress. The
  // filter is purely over the already-loaded rows — NO extra Firestore.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 140);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    const unsub = listenProjects(setLive, {
      onErr: () => toast('Could not load projects (check connection)'),
      onReady: () => setLoading(false),
    });
    return unsub;
  }, [toast]);

  // Default view hides soft-archived rows; toggle re-includes them.
  const liveVisible = live.filter(
    (p) => includeArchived || !p.archived,
  );
  const all: DemoProject[] = [
    ...liveVisible.map(liveToRow),
    ...seedProjects(),
  ];
  const scoped =
    role === 'admin'
      ? all
      : all.filter((p) => p.assigneeUid === DEMO_STAFF.uid);
  // Live substring filter over the already-loaded rows (debounced query).
  // Empty query → full list. Matched against the SAME tier text the row
  // renders, plus name / client# / VP / assignee / stage.
  const list = query
    ? scoped.filter((p) => {
        const t = templateOf(p);
        return matchesClientSearch(
          p,
          `${t.tier} · ${canonicalServiceNameFor(t.tier) ?? t.displayName}`,
          query,
        );
      })
    : scoped;

  const archivedCount = live.filter((p) => p.archived).length;

  const toolbar = (
    <>
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, number, VP, tier…"
        aria-label="Search projects"
        style={projSearchStyle}
      />
      {query && (
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {list.length} match{list.length === 1 ? '' : 'es'}
        </span>
      )}
      <label
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginLeft: 'auto',
        }}
      >
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
        />
        Include archived ({archivedCount})
      </label>
    </>
  );

  return (
    <ListScreen
      title={role === 'admin' ? 'All Projects' : 'My projects'}
      note={
        role === 'admin'
          ? 'Cross-project — admin oversight across every engagement'
          : `Assigned to ${DEMO_STAFF.name}`
      }
      toolbar={toolbar}
    >
      {loading && <ListLoading label="Loading projects…" />}
      {!loading && list.length === 0 && query && (
        <EmptyNote title="No matches">
          Nothing matches “{query}”. Clear the search to see all projects.
        </EmptyNote>
      )}
      {!loading && list.length === 0 && !query && (
        <EmptyNote title="No projects yet">
          Convert a quoted lead from the Lead Inbox to create the first
          real project, or add a project from a Client Detail screen.
        </EmptyNote>
      )}
      {!loading &&
        list.map((p) => {
          const tpl = templateOf(p);
          const m = moneyViewOf(p);
          const liveRow = live.find((x) => x.pid === p.pid);
          const isArchived = liveRow?.archived === true;
          // Master record wins over the project's cached `clientName`.
          const clientName = resolveName(
            liveRow?.clientId || p.clientNo,
            p.clientName,
          );
          return (
            <Card
              key={p.pid}
              className="row-card"
              style={isArchived ? { opacity: 0.6 } : undefined}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 10,
                }}
              >
                {/* The project's own name leads the row; the VP code +
                    client name sit as the secondary identity line below.
                    A nameless (pre-field) project falls back to a
                    `VP### · tier` placeholder so nothing looks broken. */}
                <b>
                  {projectLabel(p.projectName, p.vp, `${tpl.tier}`)}
                </b>
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  {p.vp} · {clientName} · {p.clientNo}
                </span>
                <Tag kind="val">{tpl.tier} · {canonicalServiceNameFor(tpl.tier) ?? tpl.displayName}</Tag>
                {isArchived && <Tag kind="warn">archived</Tag>}
                {role === 'admin' && (
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    assignee: {p.assigneeName}
                  </span>
                )}
                {vis.seesMoneyInternals && (
                  <span style={{ fontSize: 12 }}>
                    <b style={{ color: 'var(--good)' }}>{inr(m.paidINR)}</b>
                    {' / '}
                    {inr(m.valueINR)}
                  </span>
                )}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() => {
                      setRelinking((r) => (r === p.pid ? null : p.pid));
                      setEditing(null);
                    }}
                  >
                    {relinking === p.pid ? 'Close' : 'Re-link'}
                  </Btn3>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing((e) => (e === p.pid ? null : p.pid));
                      setRelinking(null);
                    }}
                  >
                    {editing === p.pid ? 'Close' : 'Edit'}
                  </Btn3>
                  <Link to={`/conversation/${p.pid}`}>
                    <Btn3 intent="ghost" size="sm">Conversation</Btn3>
                  </Link>
                  <Link to={`/project/${p.pid}`}>
                    <Btn3 intent="ink" size="sm">Open</Btn3>
                  </Link>
                </span>
              </div>
              <StageRail stages={railOf(p)} />
              {editing === p.pid && (
                <EditPanel
                  project={p}
                  clientId={liveRow?.clientId || p.clientNo}
                  clientName={clientName}
                  partnerId={liveRow?.partnerId ?? null}
                  archived={isArchived}
                  trashed={liveRow?.trashed === true}
                  actorName={actorName}
                  mayTrash={canManageTrash(role)}
                  allProjects={live}
                  onMsg={(msg) => toast(msg)}
                />
              )}
              {relinking === p.pid && (
                <RelinkProjectPanel
                  project={{
                    pid: p.pid,
                    vp: p.vp,
                    projectName: p.projectName,
                    clientId: liveRow?.clientId || p.clientNo,
                    clientNo: p.clientNo,
                    clientName,
                    partnerId: liveRow?.partnerId ?? null,
                  }}
                  actorName={actorName}
                  onDone={(msg) => toast(msg)}
                  onClose={() => setRelinking(null)}
                />
              )}
            </Card>
          );
        })}
    </ListScreen>
  );
}

/** Search-input style shared by the toolbar. */
const projSearchStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '7px 11px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  minWidth: 220,
  flex: '1 1 220px',
  maxWidth: 360,
};

/* ════════════════════ Per-row edit panel ════════════════════ */

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '10px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

/** Edits the project doc + its real clients/{clientNo} master, archive,
 *  Trash. ARCHIVE-FIX 2026-05-22 — archive is now project-only; it no
 *  longer flips the client master. */
function EditPanel({
  project,
  clientId,
  clientName,
  partnerId,
  archived,
  trashed,
  actorName,
  mayTrash,
  allProjects,
  onMsg,
}: {
  project: DemoProject;
  /** The project's current source-of-truth clientId (IA-fix FK). */
  clientId: string;
  /** Canonical client name (resolved from the clients/{id} master). */
  clientName: string;
  /** The project's current channel-partner link (nullable). */
  partnerId: string | null;
  /**
   * The already-streamed live project list — folded (no extra read) by
   * `validateVpEdit` to guard VP-number uniqueness on Save.
   */
  allProjects: LiveProject[];
  archived: boolean;
  /** true → the project is already in the recoverable Trash. */
  trashed: boolean;
  actorName: string;
  /** true → the signed-in user may move the project to Trash / restore. */
  mayTrash: boolean;
  onMsg: (m: string) => void;
}) {
  const tpl = templateOf(project);
  // project-doc fields
  // The project's own name — editable. Empty for a pre-field project; the
  // placeholder shows the `VP### · tier` fallback the row would display.
  const [projectName, setProjectName] = useState(project.projectName ?? '');
  // The human VP number — editable label (design 2026-07-17 §3). The doc id
  // stays the opaque key every reference uses; this only rewrites the `vp`
  // field. Uniqueness is guarded on Save via validateVpEdit.
  const [vp, setVp] = useState(project.vp ?? '');
  // VP-uniqueness rejection, surfaced inline (a11y: survives the toast timeout
  // and is programmatically linked to the input via aria-describedby). Set on
  // a Save-time validator rejection; cleared as soon as the field is edited.
  const [vpError, setVpError] = useState<string | null>(null);
  const vpErrorId = `vp-error-${project.pid}`;
  const [tier, setTier] = useState<Tier>(project.tier as Tier);
  const [valueINR, setValueINR] = useState(String(project.valueINR));
  const [assigneeName, setAssigneeName] = useState(project.assigneeName);
  const [activeStageKey, setActiveStageKey] = useState(
    project.activeStageKey,
  );
  // BUG 4 — the linked client. `linked` is the client the project is
  // CURRENTLY (or about-to-be) linked to. The ClientPicker swaps it for a
  // different EXISTING client; the displayed client number tracks it.
  const [linked, setLinked] = useState<ClientRow>({
    clientId,
    clientNo: project.clientNo,
    // Canonical master name, NOT the project's cached `clientName`.
    name: clientName,
    projectCount: 0,
    archivedCount: 0,
    totalValueINR: 0,
  });
  // contact fields of the linked client's master record (clients/{clientNo})
  const [client, setClient] = useState<LiveClient | null>(null);
  const [cPhone, setCPhone] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cLoc, setCLoc] = useState('');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load the master record for whichever client is currently linked. Runs
  // again when the picker re-links to a different client so the contact
  // fields + the displayed number reflect the SELECTED client.
  useEffect(() => {
    let alive = true;
    setClient(null);
    setCPhone('');
    setCEmail('');
    setCLoc('');
    getClient(linked.clientNo)
      .then((c) => {
        if (!alive) return;
        setClient(c);
        if (c) {
          setCPhone(c.phone);
          setCEmail(c.email);
          setCLoc(c.location);
        }
      })
      .catch(() => {
        /* client master may not exist for a project-only import. */
      });
    return () => {
      alive = false;
    };
  }, [linked.clientNo]);

  const clientChanged = linked.clientId !== clientId;

  async function save() {
    if (busy) return;
    // VP-number uniqueness guard — validate BEFORE any write, against the
    // already-loaded live list (no extra read). Block Save on a collision.
    const vpCheck = validateVpEdit(vp, allProjects, project.pid);
    if (!vpCheck.ok) {
      const msg = vpCheck.error ?? 'That VP number cannot be used.';
      setVpError(msg);
      onMsg(msg);
      return;
    }
    setVpError(null);
    setBusy(true);
    try {
      const displayName =
        canonicalTierOptions().find((o) => o.tier === tier)?.displayName ??
        tpl.displayName;
      // VP relabel — only when the number actually changed. Writes the single
      // `vp` field + its own project_vp_relabel ledger entry (audit trail).
      const currentVp = (project.vp ?? '').trim().toUpperCase();
      if (vpCheck.value !== currentVp) {
        await relabelProjectVp(project.pid, vpCheck.value, project.vp, actorName);
      }
      // BUG 4 — if the team member picked a DIFFERENT existing client,
      // re-link the project (rewrites clientId + clientNo + clientName,
      // writes the audit ledger entry). Otherwise just patch the project.
      if (clientChanged) {
        await relinkProject(
          project.pid,
          {
            clientId: linked.clientId,
            clientNo: linked.clientNo,
            clientName: linked.name,
            partnerId: partnerId ?? null,
          },
          actorName,
        );
      }
      await updateProject(
        project.pid,
        {
          projectName: projectName.trim(),
          tierCode: tier,
          tier: `${tier} · ${displayName}`,
          valueINR: Number(valueINR) || 0,
          assigneeName: assigneeName.trim(),
          activeStageKey: activeStageKey.trim(),
        },
        actorName,
      );
      // Contact edits apply to the LINKED client's master record.
      if (client) {
        await updateClient(
          client.clientNo,
          {
            phone: cPhone.trim(),
            email: cEmail.trim(),
            location: cLoc.trim(),
          },
          actorName,
        );
      }
      onMsg(
        `Saved ${project.vp}` +
          (clientChanged
            ? ` — re-linked to ${linked.clientNo} (${linked.name}).`
            : `${client ? ' + client master' : ''}.`),
      );
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doArchive() {
    if (busy) return;
    setBusy(true);
    try {
      // ARCHIVE-FIX 2026-05-22 — archive the PROJECT only. The old code
      // also flipped `archiveClient` here, which wrongly archived the
      // whole client master (and all its OTHER projects' client) whenever
      // ONE project was archived. Archive is now strictly project-scoped;
      // the client has its own archive control on Client Detail.
      await archiveProject(project.pid, actorName, !archived);
      onMsg(`${project.vp} ${archived ? 'un-archived' : 'archived'}.`);
      setConfirmArchive(false);
    } catch (e) {
      onMsg(`Archive failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /** Move the project to the recoverable Trash (or restore it). Project-
   *  only — never touches the client (relationship rule). */
  async function doTrash() {
    if (busy) return;
    setBusy(true);
    try {
      await trashProject(project.pid, actorName, !trashed);
      onMsg(
        trashed
          ? `${project.vp} restored from Trash.`
          : `${project.vp} moved to Trash (recoverable).`,
      );
      setConfirmTrash(false);
    } catch (e) {
      onMsg(`Trash failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: '1px solid var(--rule)',
      }}
    >
      {/* The project's OWN name — its identity, distinct from the client
          name and the VP code. A client may own several same-tier projects
          (e.g. an L1B for the residence and an L1B for the office); the
          project name is what tells them apart. Leaving it blank degrades
          gracefully to a `VP### · tier` placeholder. */}
      <Lbl>Project name</Lbl>
      <input
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        placeholder={projectLabel(null, project.vp, project.tier)}
        aria-label="Project name"
        style={inputStyle}
      />
      <p
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          margin: '4px 0 0',
        }}
      >
        The project's own name — e.g. "Residence — Vastu review" or "Office
        fit-out". Distinct from the client and the {project.vp} code. Leave
        blank to show <b>{projectLabel(null, project.vp, project.tier)}</b>.
      </p>

      {/* The human VP number — an EDITABLE label (design 2026-07-17 §3). The
          project's internal reference never changes, so quotes, payments and
          messages stay linked; only this human-facing number is rewritten.
          Must stay unique across projects (guarded on Save). */}
      <Lbl>VP number</Lbl>
      <input
        value={vp}
        onChange={(e) => { setVp(e.target.value.toUpperCase()); if (vpError) setVpError(null); }}
        placeholder={project.vp}
        aria-label="VP number"
        aria-invalid={vpError ? true : undefined}
        aria-describedby={vpError ? vpErrorId : undefined}
        style={vpError ? { ...inputStyle, borderColor: 'var(--crit)' } : inputStyle}
      />
      {vpError && (
        <p
          id={vpErrorId}
          role="alert"
          style={{
            fontSize: 12,
            color: 'var(--crit)',
            margin: '4px 0 0',
          }}
        >
          {vpError}
        </p>
      )}
      <p
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          margin: '4px 0 0',
        }}
      >
        The project's VP number — editable. Quotes, payments and messages stay
        linked when you change it. Must be unique across all projects.
      </p>

      {/* BUG 4 — the client field is a searchable picker over EXISTING
          clients (never a free-text "create new"). Picking one re-links the
          project; the client number below tracks the selected client. */}
      <Lbl>Client (master record)</Lbl>
      <ClientPicker
        value={linked.clientId}
        onPick={setLinked}
        ariaLabel="Search and re-link this project's client"
        placeholder="Type a client name or number to find / re-link…"
      />
      <div
        style={{
          fontSize: 12,
          color: clientChanged ? 'var(--accent-deep)' : 'var(--ink-mute)',
          marginTop: 5,
        }}
      >
        {clientChanged ? 'Will re-link to ' : 'Linked client: '}
        <b style={{ color: 'var(--ink)' }}>
          {linked.name || linked.clientId}
        </b>{' '}
        ({linked.clientNo})
      </div>
      {client ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 8,
            flexWrap: 'wrap',
          }}
        >
          <input
            value={cPhone}
            onChange={(e) => setCPhone(e.target.value)}
            placeholder="Phone"
            style={{ ...inputStyle, flex: 1, minWidth: 150 }}
          />
          <input
            value={cEmail}
            onChange={(e) => setCEmail(e.target.value)}
            placeholder="Email"
            style={{ ...inputStyle, flex: 1, minWidth: 150 }}
          />
          <input
            value={cLoc}
            onChange={(e) => setCLoc(e.target.value)}
            placeholder="Location"
            style={{ ...inputStyle, flex: 1, minWidth: 150 }}
          />
        </div>
      ) : (
        <p
          style={{
            fontSize: 12,
            color: 'var(--ink-mute)',
            marginTop: 6,
          }}
        >
          No separate client master record for {linked.clientNo} — contact
          details live on the project until a master is created.
        </p>
      )}

      <Lbl>Service tier (all imports are placeholder — confirm)</Lbl>
      <select
        value={tier}
        onChange={(e) => setTier(e.target.value as Tier)}
        style={{ ...inputStyle, minWidth: 260 }}
      >
        {canonicalTierOptions().map((o) => (
          <option key={o.tier} value={o.tier}>
            {o.tier} · {o.displayName}
          </option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <Lbl>Value (₹)</Lbl>
          <input
            value={valueINR}
            onChange={(e) =>
              setValueINR(e.target.value.replace(/[^\d]/g, ''))
            }
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <Lbl>Assignee</Lbl>
          <input
            value={assigneeName}
            onChange={(e) => setAssigneeName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <Lbl>Active stage key</Lbl>
          <input
            value={activeStageKey}
            onChange={(e) => setActiveStageKey(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Btn3 intent="grn" size="sm" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </Btn3>
        {!confirmArchive && !confirmTrash && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setConfirmArchive(true)}
            disabled={busy}
          >
            {archived ? 'Un-archive…' : 'Archive…'}
          </Btn3>
        )}
        {confirmArchive && (
          <span
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--ink-soft)' }}>
              {archived
                ? 'Un-archive this project?'
                : 'Archive — hidden from the default list (this project only; the client is untouched).'}
            </span>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setConfirmArchive(false)}
            >
              Cancel
            </Btn3>
            <Btn3 intent="ink" size="sm" onClick={doArchive} disabled={busy}>
              {archived ? 'Yes, un-archive' : 'Yes, archive'}
            </Btn3>
          </span>
        )}
        {/* Move to Trash — recoverable; distinct from archive. Project-only,
            never touches the client. Capability-gated. */}
        {mayTrash && !confirmArchive && !confirmTrash && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setConfirmTrash(true)}
            disabled={busy}
          >
            {trashed ? 'Restore from Trash…' : 'Move to Trash…'}
          </Btn3>
        )}
        {confirmTrash && (
          <span
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--ink-soft)' }}>
              {trashed
                ? 'Restore this project from the Trash?'
                : 'Move to Trash — removed from every list (recoverable from the Trash screen, never lost).'}
            </span>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setConfirmTrash(false)}
            >
              Cancel
            </Btn3>
            <Btn3 intent="ink" size="sm" onClick={doTrash} disabled={busy}>
              {trashed ? 'Yes, restore' : 'Yes, move to Trash'}
            </Btn3>
          </span>
        )}
      </div>
    </div>
  );
}
